import * as React from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { toast } from "sonner";
import { Users2, Coins, MapPin, XCircle } from "lucide-react";

/**
 * AssignmentsPanel — shows a member's current + optional past
 * assignments and lets an operator end an active one.
 *
 * Multi-site holders show two (or more) rows.  When a
 * ``rate_override_cents`` is set, it's rendered with a violet "OVR"
 * chip and mono numerals; otherwise the row shows "person rate".
 */
export default function AssignmentsPanel({ companyId, userId, canManage }) {
  const [assignments, setAssignments] = React.useState(null);
  const [includeEnded, setIncludeEnded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(
        `/companies/${companyId}/hr/members/${userId}/assignments`,
        { params: { include_ended: includeEnded } },
      );
      setAssignments(data.assignments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load assignments");
      setAssignments([]);
    }
  }, [companyId, userId, includeEnded]);

  React.useEffect(() => { load(); }, [load]);

  async function endAssignment(a) {
    if (!window.confirm(`End assignment to '${a?._position?.title || a.position_id.slice(0,8)}' today?`)) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/hr/assignments/${a.id}/end`, {
        reason: "ended from drawer",
      });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not end assignment");
    } finally { setBusy(false); }
  }

  if (assignments === null) {
    return <div className="p-3 text-[12px] text-brand-muted">Loading…</div>;
  }

  return (
    <div data-testid="assignments-panel">
      <div className="flex items-center gap-2 mb-2">
        <Users2 size={13} className="text-brand-violet" />
        <span className="micro-label">ASSIGNMENTS</span>
        <label className="ml-auto flex items-center gap-1 text-[11px] text-brand-muted">
          <input type="checkbox" className="accent-brand-violet"
                 checked={includeEnded}
                 onChange={(e) => setIncludeEnded(e.target.checked)}
                 data-testid="assignments-include-ended" />
          Show ended
        </label>
      </div>
      {assignments.length === 0 ? (
        <div className="card-panel p-3 text-[12px] text-brand-muted">No assignments.</div>
      ) : (
        <div className="card-panel overflow-hidden">
          <table className="fable-table !text-[11.5px]">
            <thead>
              <tr>
                <th>Position</th>
                <th className="w-[100px]">Site</th>
                <th className="w-[100px]">From</th>
                <th className="w-[100px]">To</th>
                <th className="w-[130px]">Rate</th>
                {canManage && <th className="w-[70px]"></th>}
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => {
                const p = a._position || {};
                const ended = !!a.valid_to;
                return (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                    <td className="font-medium text-brand-ink">{p.title || "—"}</td>
                    <td className="mono-num text-brand-muted">
                      <MapPin size={9} className="inline mr-1" />
                      {(a.site_id || "").slice(0, 8)}
                    </td>
                    <td className="mono-num text-brand-muted">{a.valid_from}</td>
                    <td className="mono-num text-brand-muted">
                      {ended ? a.valid_to : <span className="text-emerald-800">current</span>}
                    </td>
                    <td className="mono-num">
                      {a.rate_override_cents != null ? (
                        <span className="inline-flex items-center gap-1">
                          <Coins size={10} className="text-brand-violet" />
                          <span>{fmtCents(a.rate_override_cents)}</span>
                          <span className="chip chip-violet text-[9.5px] mono-num">OVR</span>
                        </span>
                      ) : (
                        <span className="text-brand-muted">person rate</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="text-right">
                        {!ended && (
                          <button className="btn-ghost h-7 !p-1 text-[10.5px] text-coral"
                                  onClick={() => endAssignment(a)} disabled={busy}
                                  data-testid={`assignment-end-${a.id}`}>
                            <XCircle size={11} /> End
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
